import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';

import { useStateContext } from '../../../contexts/ContextProvider';
import { usePresenceFil } from '../../../hooks/usePresenceFil';
import { useMesChambres } from '../../api/useMesChambres';
import {
    cleFils,
    marquerFilLu,
    masquerMessage,
    signalerFilFerme,
    signalerFilOuvert,
    useEnvoyerMessage,
    useFilsMenage,
    useMessagesFil,
    type MessageMenage,
} from '../../api/useMessagesMenage';
import EtatErreur from '../../components/EtatErreur';
import Screen from '../../components/Screen';
import Text from '../../components/Text';
import { titreFil } from '../../helpers/messagesMenage';
import { t } from '../../i18n/langue';
import EnteteFiche from '../menage/EnteteFiche';

import BarreMenage, { type ReponseEnCours } from './BarreMenage';
import BulleMenage from './BulleMenage';
import FeuilleLecteurs from './FeuilleLecteurs';

/**
 * Un fil de la messagerie ménage. L'ouvrir le marque lu (Q16) et coupe les pushs
 * de ce fil (sauf urgents) tant qu'il reste visible à l'écran.
 */
export default function FilMenage() {
    const navigate = useNavigate();
    const client = useQueryClient();
    const filId = Number(useParams<{ threadId: string }>().threadId);
    const { currentFacility, user } = useStateContext();
    const facilityId = currentFacility?.id ?? null;

    const fils = useFilsMenage(facilityId);
    const { data, isLoading, isError, refetch } = useMessagesFil(facilityId, filId);
    const chambres = useMesChambres({ facilityId });
    const envoi = useEnvoyerMessage(facilityId);
    const [reponse, setReponse] = useState<ReponseEnCours | null>(null);
    const [lecteursDe, setLecteursDe] = useState<number | null>(null);
    const [echecMasquage, setEchecMasquage] = useState(false);

    const fil = fils.data?.threads.find((f) => f.id === filId);
    const gouvernance = !!fils.data?.can.urgent;
    const dernierId = data?.data.at(-1)?.id;

    useEffect(() => {
        if (!dernierId) return;
        void marquerFilLu(filId).then(() => client.invalidateQueries(cleFils(facilityId)));
    }, [dernierId, filId, facilityId, client]);

    usePresenceFil(filId, signalerFilOuvert, signalerFilFerme);

    const actions = (message: MessageMenage) => {
        const liste: { libelle: string; onPress: () => void }[] = [];
        if (fil?.kind === 'team' && !message.is_mine && !gouvernance) {
            liste.push({ libelle: t('messagesMenage.bulle.repondreATous'), onPress: () => setReponse({ messageId: message.id, prive: false }) });
            liste.push({ libelle: t('messagesMenage.bulle.repondreEnPrive'), onPress: () => setReponse({ messageId: message.id, prive: true }) });
        }
        if (message.is_mine || message.can_moderate) {
            liste.push({ libelle: t('messagesMenage.bulle.quiALu'), onPress: () => setLecteursDe(message.id) });
        }
        // Le serveur dit où « Masquer » est permis (en inter-hôtels, pas sur les
        // messages de l'autre hôtel) ; un refus s'affiche au lieu d'être avalé.
        if (message.can_moderate && !message.hidden) {
            liste.push({
                libelle: t('messagesMenage.bulle.masquer'),
                onPress: () => {
                    setEchecMasquage(false);
                    masquerMessage(message.id)
                        .then(() => refetch())
                        .catch(() => setEchecMasquage(true));
                },
            });
        }

        return liste;
    };

    const pied = data?.can_write ? (
        <BarreMenage
            onEnvoyer={(contenu) => envoi.mutateAsync({ filId, ...contenu })}
            enVol={envoi.isLoading}
            chambres={(chambres.data ?? []).map((c) => ({ id: c.service_id, name: c.room_name }))}
            peutMarquerUrgent={gouvernance}
            reponse={reponse}
            onAnnulerReponse={() => setReponse(null)}
        />
    ) : undefined;

    return (
        <Screen
            entete={<EnteteFiche titre={fil ? titreFil(fil, user?.id) : t('messagesMenage.titre')} onRetour={() => navigate('/hk-messages')} />}
            pied={pied}
        >
            <div className="flex flex-col gap-3 px-3 py-4">
                {isLoading && (
                    <Text variante="petit" ton="attenue">
                        {t('commun.chargement')}
                    </Text>
                )}
                {isError && <EtatErreur onReessayer={() => void refetch()} />}
                {echecMasquage && (
                    <div role="alert">
                        <Text variante="petit" ton="danger">
                            {t('messagesMenage.bulle.echecMasquage')}
                        </Text>
                    </div>
                )}
                {data && data.data.length === 0 && (
                    <Text variante="petit" ton="attenue">
                        {t('messagesMenage.fil.vide')}
                    </Text>
                )}
                {data && !data.can_write && (
                    <Text variante="petit" ton="attenue">
                        {t('messagesMenage.fil.lectureSeule')}
                    </Text>
                )}
                {data?.data.map((message) => (
                    <BulleMenage key={message.id} message={message} actions={actions(message)} />
                ))}
            </div>
            {lecteursDe !== null && <FeuilleLecteurs messageId={lecteursDe} onFermer={() => setLecteursDe(null)} />}
        </Screen>
    );
}
